/**
 * Hybrid Compression Service for COSMK DRM Platform
 * Supports both local and external Cosmic Compression API
 * Controlled by VITE_USE_LOCAL_COMPRESSION environment variable
 */

export interface CompressionOptions {
  quality?: 'LOW' | 'MEDIUM' | 'HIGH' | 'FAST' | 'BALANCED' | 'QUALITY'
  preserveMetadata?: boolean
  targetSize?: number // Target file size in bytes
  maxDuration?: number // Maximum processing time in seconds
}

// Compression quality presets for optimal performance
const COMPRESSION_PRESETS = {
  FAST: {
    quality: 'LOW',
    description: 'Fastest compression, lower quality',
    estimatedTime: 0.5, // 50% of normal time
    compressionRatio: 0.8 // 80% of normal compression
  },
  BALANCED: {
    quality: 'MEDIUM', 
    description: 'Balanced speed and quality',
    estimatedTime: 1.0, // Normal time
    compressionRatio: 1.0 // Normal compression
  },
  QUALITY: {
    quality: 'HIGH',
    description: 'Best quality, slower compression',
    estimatedTime: 2.0, // 2x normal time
    compressionRatio: 1.2 // 120% of normal compression
  }
} as const

export interface CompressionResult {
  compressedFile: File
  originalSize: number
  compressedSize: number
  compressionRatio: number
  mediaId: string
  mediaUrl: string
  service: 'local' | 'external'
  jobId?: string
  serviceType?: string
}

export interface CompressionProgress {
  progress: number
  stage: 'uploading' | 'compressing' | 'downloading' | 'completed' | 'error'
  message: string
  jobId?: string
}

interface ExternalAuthResponse {
  token: string
  partyId: string
}

class CompressionService {
  private readonly useLocal = import.meta.env.VITE_USE_LOCAL_COMPRESSION === 'true'
  
  // Service URLs
  private readonly LOCAL_BASE_URL = import.meta.env.VITE_LOCAL_COMPRESSION_URL || 'http://localhost:43301/api/v1/compressor'
  private readonly EXTERNAL_BASE_URL = import.meta.env.VITE_EXTERNAL_COMPRESSION_URL || 'http://34.27.168.224/api/v1/compressor'
  
  // External service credentials
  private readonly EXTERNAL_USERNAME = 'manny@cosmicwire.com'
  private readonly EXTERNAL_PASSWORD = '123456'
  private readonly EXTERNAL_PARTY_ID = '06542ab4-1a78-11ee-b8df-fa163e0f27c0'
  private readonly EXTERNAL_API_KEY = '3acfc673-3db7-4a57-9fc6-6a229950ff2d'
  private readonly EXTERNAL_JWT_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIwOWUzMzc5YS0xZWQ2LTQ3MzMtYmM5ZC1hNmJmYzM3ZTEzNjMiLCJqdGkyIjoiMDY1NDJhYjQtMWE3OC0xMWVlLWI4ZGYtZmExNjNlMGYyN2MwIiwibm9uY2UiOjE3OTAzMDE3MSwiand0aWQiOiJhODcwMzg4Zi0xNTQwLTRjZDktODY1Mi00MDcxNGVhMTQ0ZDQiLCJpYXQiOjE3NTQ2Nzc1MzcsImV4cCI6MTc1NzI2OTUzNywiaXNzIjoiQ09TTUlDIn0.zAnSG2gIBor5CZFxwe3sVQcDst_KwASth_sdKyr2nI4'
  
  // Local service credentials
  private readonly LOCAL_API_KEY = '2e93f08c-b6b7-4690-8994-028dead9fe86' // API key from database
  private readonly LOCAL_PARTY_ID = '06542ab4-1a78-11ee-b8df-fa163e0f27b9' // Party ID from database
  
  // Polling configuration (adaptive based on service)
  private readonly LOCAL_POLL_INTERVAL = 500 // Reduced from 1000ms to 500ms for faster response
  private readonly LOCAL_MAX_ATTEMPTS = 600 // Increased from 300 to 600 for larger files (10 minutes)
  private readonly EXTERNAL_INITIAL_POLL_INTERVAL = 2000 // Reduced from 3000ms to 2000ms
  private readonly EXTERNAL_MAX_POLL_INTERVAL = 10000 // Reduced from 15000ms to 10000ms
  private readonly EXTERNAL_MAX_POLL_ATTEMPTS = 600 // Increased from 300 to 600 for larger files (20 minutes)
  private readonly EXTERNAL_FAST_POLL_DURATION = 30000 // Reduced from 60000ms to 30000ms for faster initial response
  private readonly EXTERNAL_SLOW_POLL_THRESHOLD = 60000 // Reduced from 120000ms to 60000ms
  
  private authToken: string | null = null // API token for local service, JWT token for external
  private jwtToken: string | null = null // JWT token for local service
  private tokenExpiry: number | null = null
  
  // Cancellation support
  private activeJobs = new Map<string, AbortController>()

  /**
   * Performance monitoring and metrics
   */
  private compressionMetrics = {
    totalFiles: 0,
    totalSize: 0,
    averageCompressionTime: 0,
    averageCompressionRatio: 0,
    failedCompressions: 0,
    servicePerformance: {
      local: { avgTime: 0, successRate: 0, totalJobs: 0 },
      external: { avgTime: 0, successRate: 0, totalJobs: 0 }
    }
  }

  /**
   * Record compression performance metrics
   */
  private recordMetrics(
    service: 'local' | 'external',
    fileSize: number,
    compressionTime: number,
    compressionRatio: number,
    success: boolean
  ) {
    this.compressionMetrics.totalFiles++
    this.compressionMetrics.totalSize += fileSize
    
    if (success) {
      const serviceMetrics = this.compressionMetrics.servicePerformance[service]
      serviceMetrics.totalJobs++
      
      // Update average compression time
      serviceMetrics.avgTime = (serviceMetrics.avgTime * (serviceMetrics.totalJobs - 1) + compressionTime) / serviceMetrics.totalJobs
      
      // Update average compression ratio
      this.compressionMetrics.averageCompressionRatio = 
        (this.compressionMetrics.averageCompressionRatio * (this.compressionMetrics.totalFiles - 1) + compressionRatio) / this.compressionMetrics.totalFiles
      
      // Update success rate
      serviceMetrics.successRate = (serviceMetrics.totalJobs - this.compressionMetrics.failedCompressions) / serviceMetrics.totalJobs
    } else {
      this.compressionMetrics.failedCompressions++
    }
  }

  /**
   * Get compression performance metrics
   */
  getPerformanceMetrics() {
    return {
      ...this.compressionMetrics,
      averageFileSize: this.compressionMetrics.totalFiles > 0 ? 
        this.compressionMetrics.totalSize / this.compressionMetrics.totalFiles : 0
    }
  }

  /**
   * Get recommended compression settings based on performance history
   */
  getRecommendedSettings(fileSize: number): CompressionOptions {
    const sizeMB = fileSize / (1024 * 1024)
    
    // Use performance history to recommend optimal settings
    if (sizeMB > 100) {
      return { quality: 'FAST', preserveMetadata: false } // Large files: prioritize speed
    } else if (sizeMB > 50) {
      return { quality: 'BALANCED', preserveMetadata: true } // Medium files: balanced approach
    } else {
      return { quality: 'QUALITY', preserveMetadata: true } // Small files: prioritize quality
    }
  }

  constructor() {
    console.log(`🔧 Compression service initialized: ${this.useLocal ? 'LOCAL' : 'EXTERNAL'} mode`)
    console.log(`📍 Service URL: ${this.useLocal ? this.LOCAL_BASE_URL : this.EXTERNAL_BASE_URL}`)
  }

  /**
   * Authenticate with compression service
   */
  private async authenticate(): Promise<string> {
    if (this.useLocal) {
      // For local service, we need a two-step authentication process:
      // 1. Login to get JWT token
      // 2. Create API token using JWT token
      try {
        console.log('🔐 Authenticating with local compression service...')
        
        // Step 1: Login to get JWT token
        const loginResponse = await fetch(`${this.LOCAL_BASE_URL}/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: 'a@b.com',
            password: '123456'
          })
        })

        if (!loginResponse.ok) {
          throw new Error(`Local login failed: ${loginResponse.status} ${loginResponse.statusText}`)
        }

        const loginData = await loginResponse.json()
        
        if (!loginData || !loginData.token) {
          throw new Error('Invalid login response from local service')
        }

        const jwtToken = loginData.token
        console.log('✅ Local compression service login successful')

        // Step 2: Create API token using JWT token
        const apiTokenResponse = await fetch(`${this.LOCAL_BASE_URL}/createAPIKey`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwtToken}`
          },
          body: JSON.stringify({
            username: 'a@b.com',
            password: '123456'
          })
        })

        if (!apiTokenResponse.ok) {
          throw new Error(`API token creation failed: ${apiTokenResponse.status} ${apiTokenResponse.statusText}`)
        }

        const apiTokenData = await apiTokenResponse.json()
        
        if (apiTokenData && apiTokenData.apiKey) {
          this.authToken = apiTokenData.apiKey // Store API token
          this.jwtToken = jwtToken // Store JWT token
          this.tokenExpiry = Date.now() + (60 * 60 * 1000) // 1 hour
          console.log('✅ Local compression service API token creation successful')
          return this.authToken!
        } else {
          throw new Error('Invalid API token response from local service')
        }
      } catch (error) {
        console.error('❌ Local compression service authentication failed:', error)
        throw new Error(`Local authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    }

    try {
      console.log('🔐 Authenticating with external compression service...')
      
      const response = await fetch(`${this.EXTERNAL_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: this.EXTERNAL_USERNAME,
          password: this.EXTERNAL_PASSWORD
        })
      })

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data && data.token) {
        this.authToken = data.token
        this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000) // 24 hours
        console.log('✅ External compression service authentication successful')
        return this.authToken!
      } else {
        throw new Error('Invalid authentication response from external service')
      }
    } catch (error) {
      console.error('❌ External compression service authentication failed:', error)
      throw new Error(`Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Ensure we have a valid authentication token
   */
  private async ensureAuthenticated(): Promise<string> {
    if (!this.authToken || (this.tokenExpiry && Date.now() >= this.tokenExpiry)) {
      await this.authenticate()
    }
    return this.authToken!
  }

  /**
   * Get JWT token for local service
   */
  private async getJWTToken(): Promise<string> {
    if (!this.jwtToken || (this.tokenExpiry && Date.now() >= this.tokenExpiry)) {
      await this.authenticate()
    }
    return this.jwtToken!
  }

  /**
   * Cancel an active compression job
   */
  cancelJob(jobId: string): void {
    const controller = this.activeJobs.get(jobId)
    if (controller) {
      controller.abort()
      this.activeJobs.delete(jobId)
      console.log(`🚫 Cancelled compression job: ${jobId}`)
    }
  }

  /**
   * Cancel all active compression jobs
   */
  cancelAllJobs(): void {
    for (const [jobId, controller] of this.activeJobs) {
      controller.abort()
      console.log(`🚫 Cancelled compression job: ${jobId}`)
    }
    this.activeJobs.clear()
  }

  /**
   * Compress a file using the selected compression service
   */
  async compressFile(
    file: File, 
    options: CompressionOptions = { quality: 'MEDIUM', preserveMetadata: true },
    onProgress?: (progress: CompressionProgress) => void
  ): Promise<CompressionResult> {
    
    const serviceType = this.useLocal ? 'LOCAL' : 'EXTERNAL'
    console.log(`🗜️ Starting ${serviceType} compression for ${file.name} (${this.formatFileSize(file.size)})`)
    
    onProgress?.({
      progress: 0,
      stage: 'uploading',
      message: `Preparing file for ${serviceType.toLowerCase()} compression...`
    })

    const maxRetries = 3
    let lastError: Error | null = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        onProgress?.({
          progress: 10,
          stage: 'uploading',
          message: `Uploading to ${serviceType.toLowerCase()} service... (attempt ${attempt}/${maxRetries})`
        })

        // Authenticate with service
        await this.ensureAuthenticated()

        // Prepare form data
        const formData = new FormData()
        formData.append('uploadFile', file)
        if (options.quality) {
          formData.append('compressorLevel', options.quality)
        }
        formData.append('name', file.name.replace(/\.[^/.]+$/, "")) // Remove extension
        formData.append('partyId', this.useLocal ? this.LOCAL_PARTY_ID : this.EXTERNAL_PARTY_ID)

        console.log(`📤 Sending file to ${serviceType} compression service: ${file.name} (${file.size} bytes)`)

        // Prepare headers
        const headers: Record<string, string> = {}
        if (this.useLocal) {
          // For local service, we need both JWT token and API token
          // The JWT token goes in Authorization header, API token in cwc_api_key header
          const jwtToken = await this.getJWTToken()
          headers['Authorization'] = `Bearer ${jwtToken}`
          headers['cwc_api_key'] = this.authToken // API token
        } else {
          headers['Authorization'] = `Bearer ${this.authToken}`
          headers['CWC_API_KEY'] = this.EXTERNAL_API_KEY
        }

        // Send to compression service
        const baseUrl = this.useLocal ? this.LOCAL_BASE_URL : this.EXTERNAL_BASE_URL
        const startTime = Date.now()
        const response = await fetch(`${baseUrl}/compressMedia`, {
          method: 'POST',
          headers,
          body: formData
        })
        const endTime = Date.now()
        const compressionTime = endTime - startTime

        if (!response.ok) {
          throw new Error(`Compression submission failed: ${response.status} ${response.statusText}`)
        }

        const result = await response.json()

        if (!(result.responseStatus || result.success)) {
          throw new Error(result.responseMessage || result.error || 'Compression submission failed')
        }

        const jobId = result.mediaId || result.jobId
        const mediaUrl = result.mediaUrlCompressor || result.mediaUrl || ''

        // Create abort controller for this job
        const abortController = new AbortController()
        this.activeJobs.set(jobId, abortController)

        onProgress?.({
          progress: 30,
          stage: 'compressing',
          message: `File compression in progress on ${serviceType.toLowerCase()} service...`,
          jobId: jobId
        })

        console.log(`✅ Compression job submitted to ${serviceType} service: ${jobId}`)

        try {
          // Wait for completion and download result
          const compressedFile = await this.waitForCompletion(
            jobId, 
            mediaUrl,
            file.size,
            onProgress,
            abortController.signal
          )
          
          // Clean up the job tracking
          this.activeJobs.delete(jobId)
          
          return {
            compressedFile,
            originalSize: file.size,
            compressedSize: compressedFile.size,
            compressionRatio: ((file.size - compressedFile.size) / file.size) * 100,
            mediaId: jobId,
            mediaUrl: mediaUrl,
            service: 'external' as const,
            jobId: jobId,
            serviceType: serviceType.toLowerCase()
          }
        } catch (error) {
          // Clean up the job tracking on error
          this.activeJobs.delete(jobId)
          
          // Check if this was an abort error
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Compression job was cancelled')
          }
          
          throw error
        }

      } catch (error) {
        lastError = error as Error
        console.warn(`${serviceType} compression attempt ${attempt} failed:`, error)
        this.recordMetrics(this.useLocal ? 'local' : 'external', file.size, 0, 0, false)

        if (attempt < maxRetries) {
          // Exponential backoff with jitter
          const delay = Math.min(1000 * Math.pow(2, attempt - 1) + Math.random() * 1000, 10000)
          
          onProgress?.({
            progress: 0,
            stage: 'uploading',
            message: `Retrying ${serviceType.toLowerCase()} compression in ${Math.round(delay / 1000)} seconds...`
          })
          
          await this.sleep(delay)
        }
      }
    }

    // All retries failed
    const finalError = lastError || new Error(`${serviceType} compression failed after multiple attempts`)
    
    onProgress?.({
      progress: 0,
      stage: 'error',
      message: this.getErrorMessage(finalError)
    })
    
    throw new Error(this.getErrorMessage(finalError))
  }

  /**
   * Check if a file should be compressed based on type and size
   */
  shouldCompress(file: File): boolean {
    const supportedTypes = [
      'video/mp4',
      'audio/mpeg', 
      'audio/mp3',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
      'application/pdf',
      'model/gltf-binary' // GLB files
    ]
    
    const isSupported = supportedTypes.includes(file.type) || 
                       supportedTypes.some(type => file.name.toLowerCase().endsWith(type.split('/')[1]))
    
    const minSize = 1024 * 1024 // 1MB threshold
    
    return isSupported && file.size > minSize
  }

  /**
   * Get estimated compression savings for a file
   */
  getEstimatedSavings(file: File): number {
    const type = file.type.toLowerCase()
    
    if (type.includes('video')) return 35 // 35% average for MP4
    if (type.includes('audio')) return 20 // 20% average for MP3
    if (type.includes('jpeg') || type.includes('jpg')) return 30 // 30% for JPEG
    if (type.includes('png')) return 25 // 25% for PNG
    if (type.includes('pdf')) return 50 // 50% for PDF
    if (type.includes('webp')) return 20 // 20% for WebP
    
    return 25 // Default estimate
  }

  /**
   * Wait for compression to complete and download the result
   * Implements persistent polling that continues even after initial timeout
   */
  private async waitForCompletion(
    jobId: string,
    mediaUrl: string,
    fileSize: number,
    onProgress?: (progress: CompressionProgress) => void,
    abortSignal?: AbortSignal
  ): Promise<File> {
    
    const initialMaxAttempts = this.useLocal ? this.LOCAL_MAX_ATTEMPTS : this.EXTERNAL_MAX_POLL_ATTEMPTS
    const startTime = Date.now()
    let attempts = 0
    let hasTimedOut = false
    let timeoutMessage = ''
    
    // For local service, we'll continue polling until completion or max timeout
    const shouldContinuePolling = this.useLocal
    const MAX_ABSOLUTE_TIMEOUT = 30 * 60 * 1000 // 30 minutes absolute maximum
    
    while (true) {
      // Check if the operation was aborted
      if (abortSignal?.aborted) {
        throw new Error('Compression job was cancelled')
      }
      
      attempts++
      const elapsed = Date.now() - startTime
      
      try {
        // Calculate polling interval - use adaptive intervals based on file size and timeout status
        let pollInterval: number
        if (hasTimedOut) {
          // After timeout, use longer intervals to reduce server load
          pollInterval = this.useLocal ? 5000 : 10000 // 5s for local, 10s for external
        } else {
          pollInterval = this.useLocal ? 
            this.LOCAL_POLL_INTERVAL : 
            this.getAdaptivePollInterval(elapsed, attempts, fileSize)
        }
        
        // Provide progress feedback
        let progressMessage = this.getProgressMessage(attempts, elapsed, this.useLocal, hasTimedOut);
        
        // Adjust progress calculation for persistent polling
        let progressPercentage: number
        if (hasTimedOut) {
          // After timeout, show slower progress to indicate extended waiting
          progressPercentage = Math.min(85, 80 + (attempts / 100) * 5)
        } else {
          progressPercentage = this.useLocal ? 
            30 + (attempts / initialMaxAttempts) * 50 : // Linear for local
            Math.min(30 + (elapsed / (15 * 60 * 1000)) * 50, 85); // Time-based for external
        }
        
        onProgress?.({
          progress: progressPercentage,
          stage: 'compressing',
          message: progressMessage
        })

        // Check compression status
        console.log(`🔍 Polling attempt ${attempts} (${this.formatDuration(elapsed)}): Checking ${this.useLocal ? 'LOCAL' : 'EXTERNAL'} status for job ${jobId}${hasTimedOut ? ' [PERSISTENT MODE]' : ''}`);
        const statusResult = await this.getServiceStatus(jobId)
        console.log(`📊 Status result for job ${jobId}:`, {
          status: statusResult.status,
          downloadUrl: statusResult.downloadUrl ? 'present' : 'missing',
          service: this.useLocal ? 'LOCAL' : 'EXTERNAL'
        });
        
        // Add detailed logging for debugging
        if (this.useLocal) {
          console.log(`🔍 Local service status check - Job ID: ${jobId}, Status: ${statusResult.status}, Download URL: ${statusResult.downloadUrl || 'none'}`)
        }
        
        if (statusResult.status === 'completed') {
          if (!statusResult.downloadUrl) {
            console.warn(`⚠️ Job ${jobId} marked as completed but no download URL provided`)
            // Try to continue polling in case URL becomes available
            if (attempts < initialMaxAttempts) {
              await this.sleep(pollInterval)
              continue
            } else {
              throw new Error(`Compression completed but no download URL available for job ${jobId}`)
            }
          }

          onProgress?.({
            progress: 90,
            stage: 'downloading',
            message: `Downloading compressed file... (${this.useLocal ? 'LOCAL' : 'EXTERNAL'} took ${this.formatDuration(elapsed)})`
          })

          console.log(`📥 Starting download from ${this.useLocal ? 'local' : 'external'} service:`, statusResult.downloadUrl)

          try {
            // Download from service
            const downloadResponse = await this.downloadFromService(statusResult.downloadUrl)
            const blob = await downloadResponse.blob()
            
            if (blob.size === 0) {
              throw new Error('Downloaded file is empty')
            }
            
            const filename = this.extractFilenameFromUrl(statusResult.downloadUrl) || 
                           this.extractFilenameFromUrl(mediaUrl) || 
                           `compressed_${jobId}.${this.getFileExtensionFromType(blob.type)}`
            
            onProgress?.({
              progress: 100,
              stage: 'completed',
              message: `Compression completed successfully! (${this.formatDuration(elapsed)} on ${this.useLocal ? 'local' : 'external'} service)${hasTimedOut ? ' - Extended polling successful!' : ''}`
            })

            console.log(`✅ ${this.useLocal ? 'Local' : 'External'} download successful after ${this.formatDuration(elapsed)}${hasTimedOut ? ' (completed via persistent polling)' : ''} - File size: ${blob.size} bytes`)
            return new File([blob], filename, { type: blob.type })
          } catch (downloadError) {
            console.error(`❌ Download failed for job ${jobId}:`, downloadError)
            throw new Error(`Download failed: ${downloadError instanceof Error ? downloadError.message : 'Unknown download error'}`)
          }
        }

        // Check absolute timeout (safety mechanism)
        if (elapsed >= MAX_ABSOLUTE_TIMEOUT) {
          const errorMsg = `Compression failed after ${this.formatDuration(elapsed)} - Maximum processing time exceeded. The file may be too large or the service may be experiencing issues.`
          throw new Error(errorMsg)
        }
        
        // Check if we should timeout (only for initial timeout, not for persistent polling)
        if (!hasTimedOut && attempts >= initialMaxAttempts) {
          hasTimedOut = true
          timeoutMessage = `Initial timeout after ${this.formatDuration(elapsed)} - continuing to poll for completion...`
          console.log(`⏰ ${timeoutMessage}`)
          
          // For external service, we don't continue polling after timeout
          if (!shouldContinuePolling) {
            const errorMsg = `Compression timeout after ${this.formatDuration(elapsed)} - The external service took too long. This may be due to high server load. Please try again later or use a smaller file.`
            throw new Error(errorMsg)
          }
          
          // For local service, continue polling with longer intervals
          continue
        }

        // Wait before next attempt
        await this.sleep(pollInterval)

      } catch (error) {
        console.warn(`Polling attempt ${attempts} failed after ${this.formatDuration(elapsed)}:`, error)
        
        // Handle authentication errors
        if ((error as Error).message.includes('authentication') || (error as Error).message.includes('401')) {
          console.log('🔄 Authentication error, will retry with fresh token...')
          this.authToken = null; // Force re-authentication
        }
        
        // For persistent polling, we don't give up on network errors
        if (hasTimedOut && shouldContinuePolling) {
          console.log('🔄 Network error in persistent mode, will retry...')
          await this.sleep(5000) // Wait 5 seconds before retry
          continue
        }
        
        // For non-persistent mode or before timeout, use normal error handling
        if (!hasTimedOut && attempts >= initialMaxAttempts) {
          const errorMsg = `Compression timeout after ${this.formatDuration(elapsed)} - The ${this.useLocal ? 'local' : 'external'} service took too long. ${this.useLocal ? 'Check if the local compression service is running properly.' : 'This may be due to high server load. Please try again later or use a smaller file.'}`
          throw new Error(errorMsg)
        }
        
        // Use appropriate interval for error cases
        const pollInterval = this.useLocal ? 
          this.LOCAL_POLL_INTERVAL : 
          this.getAdaptivePollInterval(elapsed, attempts)
        await this.sleep(pollInterval)
      }
    }
  }

  /**
   * Get compression status from the appropriate service
   */
  private async getServiceStatus(jobId: string): Promise<{status: string, downloadUrl?: string}> {
    await this.ensureAuthenticated()

    const maxRetries = 3
    let lastError: Error | null = null

    for (let retry = 1; retry <= maxRetries; retry++) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), this.useLocal ? 10000 : 30000)

        let response: Response
        if (this.useLocal) {
          // Local service endpoint - use same authentication as compression request
          const jwtToken = await this.getJWTToken()
          response = await fetch(`${this.LOCAL_BASE_URL}/status/${jobId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${jwtToken}`,
              'cwc_api_key': this.authToken // API token
            },
            signal: controller.signal
          })
        } else {
          // External service endpoint - use proper status endpoint
          response = await fetch(`${this.EXTERNAL_BASE_URL}/status/${jobId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${this.authToken}`,
              'CWC_API_KEY': this.EXTERNAL_API_KEY
            },
            signal: controller.signal
          })
        }

        clearTimeout(timeoutId)

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error')
          
          if (response.status === 401 || response.status === 403) {
            console.log(`🔄 Authentication expired for ${this.useLocal ? 'local' : 'external'} service, forcing re-auth...`)
            this.authToken = null // Force re-authentication
            if (this.useLocal) this.jwtToken = null // Also clear JWT for local service
            await this.ensureAuthenticated()
            continue // Retry with fresh token
          }
          
          console.warn(`❌ Status check failed for ${this.useLocal ? 'local' : 'external'} service:`, {
            status: response.status,
            statusText: response.statusText,
            jobId,
            errorText: errorText.substring(0, 200) // Log first 200 chars of error
          })
          
          throw new Error(`Status check failed: ${response.status} ${response.statusText} - ${errorText}`)
        }

        const data = await response.json()

        // Standardized response handling for both local and external services
        // Both services now use the same /status/:jobId endpoint format
        if (data && (data.responseStatus || data.success)) {
          const status = data.status || 'processing'
          
          // Log the response for debugging
          console.log(`📊 ${this.useLocal ? 'Local' : 'External'} service status response:`, {
            jobId,
            status,
            downloadUrl: data.downloadUrl,
            rawData: data
          })
          
          return {
            status: status === 'completed' ? 'completed' : 'processing',
            downloadUrl: data.downloadUrl
          }
        } else if (!this.useLocal && Array.isArray(data)) {
          // Fallback for external service if it still returns array format
          console.log(`⚠️ External service returned unexpected array format, falling back to legacy parsing`)
          const file = data.find(f => f.id === jobId || f.mediaId === jobId)
          
          if (file) {
            const isCompleted = file.compressed === true || 
                               file.status === 'completed' || 
                               file.downloadUrl || 
                               file.url ||
                               (file.status && file.status.toLowerCase().includes('complete'))
            
            if (isCompleted) {
              return {
                status: 'completed',
                downloadUrl: file.downloadUrl || file.url
              }
            }
          }
        }

        return { status: 'processing' }

      } catch (error) {
        lastError = error as Error
        console.warn(`Status check attempt ${retry}/${maxRetries} failed:`, error)

        if (retry < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, retry - 1), 5000)
          await this.sleep(delay)
        }
      }
    }

    // If all retries failed, assume still processing
    console.warn(`Status check failed after ${maxRetries} attempts, assuming still processing:`, lastError)
    return { status: 'processing' }
  }

  /**
   * Download file from the appropriate service
   */
  private async downloadFromService(downloadUrl: string): Promise<Response> {
    await this.ensureAuthenticated()

    const headers: Record<string, string> = {
      'Accept': 'application/octet-stream, audio/mpeg, */*'
    }

    if (this.useLocal) {
      // Use same authentication as compression and status requests
      const jwtToken = await this.getJWTToken()
      headers['Authorization'] = `Bearer ${jwtToken}`
      headers['cwc_api_key'] = this.authToken // API token
    } else {
      headers['Authorization'] = `Bearer ${this.authToken}`
      headers['CWC_API_KEY'] = this.EXTERNAL_API_KEY
    }

    const timeout = this.useLocal ? 60000 : 120000
    const response = await fetch(downloadUrl, {
      method: 'GET',
      headers,
      signal: AbortSignal.timeout(timeout)
    })

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`)
    }

    return response
  }

  /**
   * Calculate adaptive polling interval based on file size and elapsed time
   */
  private getAdaptivePollInterval(elapsed: number, _attempts: number, fileSize?: number): number {
    // Base interval calculation
    let baseInterval = this.EXTERNAL_INITIAL_POLL_INTERVAL
    
    // Adjust based on file size
    if (fileSize) {
      const sizeMB = fileSize / (1024 * 1024)
      if (sizeMB > 100) {
        // Large files (>100MB) - longer intervals
        baseInterval = Math.max(baseInterval * 2, this.EXTERNAL_MAX_POLL_INTERVAL)
      } else if (sizeMB > 50) {
        // Medium files (50-100MB) - moderate intervals
        baseInterval = Math.max(baseInterval * 1.5, this.EXTERNAL_MAX_POLL_INTERVAL * 0.75)
      }
    }
    
    // Adjust based on elapsed time
    if (elapsed < this.EXTERNAL_FAST_POLL_DURATION) {
      // Fast polling for first 30 seconds
      return Math.min(baseInterval, this.EXTERNAL_INITIAL_POLL_INTERVAL)
    } else if (elapsed < this.EXTERNAL_SLOW_POLL_THRESHOLD) {
      // Moderate polling for next 30 seconds
      return Math.min(baseInterval * 1.5, this.EXTERNAL_MAX_POLL_INTERVAL * 0.5)
    } else {
      // Slow polling after 1 minute
      return this.EXTERNAL_MAX_POLL_INTERVAL
    }
  }

  /**
   * Get user-friendly progress message
   */
  private getProgressMessage(_attempts: number, elapsed: number, isLocal: boolean, hasTimedOut?: boolean): string {
    const duration = this.formatDuration(elapsed)
    
    if (isLocal) {
      if (hasTimedOut) {
        // Persistent polling messages for local service
        if (elapsed < 600000) return `Extended compression time... (${duration}) - Large file processing`
        if (elapsed < 900000) return `Still compressing large file... (${duration}) - Please be patient`
        if (elapsed < 1200000) return `Processing complex file... (${duration}) - Almost complete`
        return `Final compression stage... (${duration}) - Will complete soon`
      } else {
        // Normal local compression messages
        if (elapsed < 10000) return `Processing file locally... (${duration})`
        if (elapsed < 30000) return `Local compression in progress... (${duration})`
        return `Almost done with local compression... (${duration})`
      }
    } else {
      if (elapsed < 30000) return `Processing file... (${duration})`
      if (elapsed < 60000) return `Compression in progress... (${duration}) - External service is working`
      if (elapsed < 120000) return `Still compressing... (${duration}) - Large files take longer`
      if (elapsed < 300000) return `External service is busy... (${duration}) - Please wait`
      if (elapsed < 600000) return `Processing large file... (${duration}) - This may take up to 15 minutes`
      return `Almost done... (${duration}) - Final processing stage`
    }
  }

  /**
   * Format duration in human-readable format
   */
  private formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`
    } else {
      return `${seconds}s`
    }
  }

  /**
   * Extract filename from media URL
   */
  private extractFilenameFromUrl(url: string): string | null {
    try {
      const urlParts = url.split('/')
      const filename = urlParts[urlParts.length - 1]
      // Remove query parameters if present
      const cleanFilename = filename.split('?')[0]
      return cleanFilename.includes('.') ? cleanFilename : null
    } catch {
      return null
    }
  }

  /**
   * Get file extension from MIME type
   */
  private getFileExtensionFromType(mimeType: string): string {
    const mimeToExt: Record<string, string> = {
      'audio/mpeg': 'mp3',
      'audio/mp3': 'mp3',
      'video/mp4': 'mp4',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/gif': 'gif',
      'application/pdf': 'pdf',
      'model/gltf-binary': 'glb'
    }
    
    return mimeToExt[mimeType] || 'bin'
  }

  /**
   * Sleep utility for polling
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  private formatFileSize = CompressionService.formatFileSize

  /**
   * Check compression service health with service awareness
   */
  private async checkServiceHealth(): Promise<{ healthy: boolean; responseTime?: number; error?: string }> {
    const startTime = Date.now()
    
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), this.useLocal ? 5000 : 10000)
      const baseUrl = this.useLocal ? this.LOCAL_BASE_URL : this.EXTERNAL_BASE_URL

      const response = await fetch(baseUrl, {
        method: 'GET',
        signal: controller.signal
      })

      clearTimeout(timeoutId)
      const responseTime = Date.now() - startTime
      
      return {
        healthy: response.ok,
        responseTime,
        error: response.ok ? undefined : `HTTP ${response.status}: ${response.statusText}`
      }
    } catch (error) {
      const responseTime = Date.now() - startTime
      console.warn(`${this.useLocal ? 'Local' : 'External'} compression service health check failed:`, error)
      
      return {
        healthy: false,
        responseTime,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get user-friendly error message
   */
  private getErrorMessage(error: Error): string {
    if (error.name === 'AbortError') {
      return `${this.useLocal ? 'Local' : 'External'} compression service timeout - please try again${this.useLocal ? ' (check if local service is running)' : ' with a smaller file'}`
    }
    
    if (error.message.includes('Failed to fetch')) {
      return `Unable to connect to ${this.useLocal ? 'local' : 'external'} compression service - ${this.useLocal ? 'check if the service is running' : 'please check your internet connection'}`
    }
    
    if (error.message.includes('401') || error.message.includes('403')) {
      return `Authentication failed with ${this.useLocal ? 'local' : 'external'} compression service`
    }
    
    if (error.message.includes('413')) {
      return 'File too large for compression - please try a smaller file'
    }
    
    if (error.message.includes('429')) {
      return `${this.useLocal ? 'Local' : 'External'} compression service is busy - please try again in a few minutes`
    }
    
    if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
      return `${this.useLocal ? 'Local' : 'External'} compression service is temporarily unavailable - please try again later`
    }

    return error.message || 'Unknown compression error occurred'
  }

  /**
   * Check if compression is available with service-specific health information
   */
  async isCompressionAvailable(): Promise<{ available: boolean; reason?: string; responseTime?: number }> {
    const healthCheck = await this.checkServiceHealth()
    
    if (!healthCheck.healthy) {
      let reason = `${this.useLocal ? 'Local' : 'External'} compression service is currently unavailable`
      
      if (this.useLocal && (healthCheck.error?.includes('timeout') || healthCheck.error?.includes('fetch'))) {
        reason = 'Local compression service is not running - start it with Docker profiles or switch to external service'
        return {
          available: false,
          reason,
          responseTime: healthCheck.responseTime
        }
      }
      
      if (!this.useLocal && (healthCheck.error?.includes('timeout') || healthCheck.error?.includes('abort'))) {
        reason = 'External compression service is responding slowly - compression may take longer than usual'
        return { 
          available: true, 
          reason: `Warning: ${reason}`,
          responseTime: healthCheck.responseTime 
        }
      }
      
      return {
        available: false,
        reason: `${reason}${healthCheck.error ? ` (${healthCheck.error})` : ''}`,
        responseTime: healthCheck.responseTime
      }
    }

    // Service is healthy
    let warning: string | undefined
    if (this.useLocal) {
      warning = healthCheck.responseTime && healthCheck.responseTime > 2000 ? 
        'Local service is responding slowly' : undefined
    } else {
      warning = healthCheck.responseTime && healthCheck.responseTime > 5000 ? 
        'External service is responding slowly - compression may take longer than usual' : undefined
    }

    return { 
      available: true,
      reason: warning,
      responseTime: healthCheck.responseTime 
    }
  }

  /**
   * Get compression quality settings information
   */
  static getQualityInfo() {
    return {
      LOW: {
        description: 'Maximum compression, smaller file size',
        estimatedSavings: '40-60%',
        qualityLoss: 'Noticeable',
        estimatedTime: 'Local: 30s-2min, External: 2-8min'
      },
      MEDIUM: {
        description: 'Balanced compression and quality',
        estimatedSavings: '25-40%', 
        qualityLoss: 'Minimal',
        estimatedTime: 'Local: 1-3min, External: 3-10min'
      },
      HIGH: {
        description: 'Minimal compression, best quality',
        estimatedSavings: '10-25%',
        qualityLoss: 'Nearly imperceptible',
        estimatedTime: 'Local: 2-5min, External: 4-12min'
      }
    }
  }

  /**
   * Get service performance information for users
   */
  static getServiceInfo() {
    return {
      local: {
        name: 'Local Compression Service',
        description: 'Fast compression using your local development environment',
        performance: {
          smallFiles: 'Under 10MB: typically 30 seconds - 2 minutes',
          mediumFiles: '10-50MB: typically 1-5 minutes',
          largeFiles: 'Over 50MB: may take 5-10 minutes',
          note: 'Processing times depend on your computer\'s performance'
        },
        reliability: {
          retries: 'Automatic retry with fast polling',
          timeout: '1 minute maximum processing time',
          statusUpdates: 'Real-time progress tracking'
        },
        requirements: [
          'Local compression service must be running',
          'Docker containers must be started with local-compression profile',
          'Sufficient local disk space and CPU resources'
        ]
      },
      external: {
        name: 'External Cosmic Compression Service',
        description: 'Professional-grade compression powered by external infrastructure',
        performance: {
          smallFiles: 'Under 10MB: typically 2-5 minutes',
          mediumFiles: '10-50MB: typically 5-10 minutes',
          largeFiles: 'Over 50MB: may take 10-15 minutes',
          note: 'Processing times vary based on server load and file complexity'
        },
        reliability: {
          retries: 'Automatic retry with exponential backoff',
          timeout: '15 minutes maximum processing time',
          statusUpdates: 'Real-time progress tracking'
        },
        tips: [
          'Larger files take significantly longer to process',
          'The service may be slower during peak usage hours',
          'You can safely close and reopen the browser - compression continues on the server',
          'Consider using MEDIUM quality for the best balance of size and speed'
        ]
      }
    }
  }
}

// Export singleton instance
export const compressionService = new CompressionService()